import type { FC } from "react";
import { memo, useCallback } from "react";

import { UiCrossIcon } from "@jti/brand-icons/ui-cross";
import { UiInfoCircleSolidIcon } from "@jti/brand-icons/ui-info-circle-solid";
import { Button } from "@jti/core-components/components/Button";
import { Card } from "@jti/core-components/components/Card";
import { type CartCoupon } from "@jti/magento";
import type { FieldValues } from "@jti/ui";
import { Form, FormField, Tooltip, useForm } from "@jti/ui";
import { useTranslations } from "@jti/ui/providers/useTranslations";

import { PromoCodeControls } from "./components/PromoCodeControls";

import styles from "./PromoCodeView.module.scss";

export type PromoCodeViewProps = {
  items: CartCoupon[];
  errors?: string[];
  onRemoveAllCoupons(): void;
  onRemoveCoupon(code: string): void;
  onAddCoupon(code: string): void;
  onFocusInput?(): void;
  isAttemptsLimitReached: boolean;
  isPending?: boolean;
};

interface FormValues extends FieldValues {
  code: string;
}

const PROMO_CODES_LIMIT = 3;

export const PromoCodeView: FC<PromoCodeViewProps> = memo(
  ({
    items,
    errors,
    onRemoveAllCoupons,
    onAddCoupon,
    onRemoveCoupon,
    onFocusInput,
    isAttemptsLimitReached,
    isPending = false,
  }) => {
    const { __ } = useTranslations();

    const form = useForm<FormValues>({
      defaultValues: {
        code: "",
      },
    });

    const { reset } = form;

    const handleSubmit = useCallback(
      async ({ code }: FormValues) => {
        await onAddCoupon(code);
        reset();
      },
      [onAddCoupon, reset]
    );

    const description = (
      <div className={styles.titleContainer}>
        <div className={styles.subtitle}>
          <div className={styles.subtitleText}>
            {__("cart.payment_summary.voucher.subtitle_closed")}{" "}
            <Tooltip
              IconSlot={() => (
                <div className={styles.infoIcon}>
                  <UiInfoCircleSolidIcon />
                </div>
              )}
              wrapperClassName={styles.tooltipWrapper}
              content={<span>{__("cart.payment_summary.voucher.subtitle_closed_popover")}</span>}
            />
          </div>
        </div>
      </div>
    );

    return (
      <Card
        variant="level-2"
        title={__("cart.payment_summary.voucher.title")}
        nonCollapsibleContent={description}
        data-testid="promoCodeCard"
        isCollapsible
        defaultCollapsed={items.length === 0}
      >
        <div className={styles.content}>
          {items.length < PROMO_CODES_LIMIT && (
            <Form form={form} onSubmit={handleSubmit} data-testid="promoCodeForm" className={styles.codeForm}>
              <FormField
                name="code"
                render={({ field }) => (
                  <PromoCodeControls
                    {...field}
                    isPending={isPending}
                    disabled={isAttemptsLimitReached}
                    onFocus={onFocusInput}
                    isError={!!errors?.length}
                    errorText={errors?.join("")}
                  />
                )}
              />
            </Form>
          )}

          {items.length > 0 ? (
            <div className={styles.itemsContainer}>
              <div className={styles.listHeader}>
                <div className={styles.appliedPromoCodes}>{__("cart.promo_code.applied_codes")}:</div>
                <Button variant="text" onClick={onRemoveAllCoupons} data-testid="removeCoupons">
                  {__("cart.promo_code.clear_all_button")}
                </Button>
              </div>
              {items.map(({ label, code: coupon_code }) => (
                <div className={styles.item} key={coupon_code}>
                  <div className={styles.couponText}>
                    <strong className={styles.couponCode}>{coupon_code}</strong>{" "}
                    {label && <span className={styles.couponLabel}>{`(${label})`}</span>}
                  </div>
                  <button
                    type="button"
                    onClick={() => onRemoveCoupon(coupon_code)}
                    data-testid={`removeCoupon${coupon_code}`}
                    className={styles.removeBtn}
                    aria-label={__("cart.promo_code.remove_coupon", [coupon_code])}
                  >
                    {<UiCrossIcon width={20} height={20} aria-hidden="true" />}
                  </button>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      </Card>
    );
  }
);
